import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';

const ScheduledPostFilter = ({ handleApplyFilter, fromDate, toDate }) => {
  const { t: translate } = useTranslation();
  const [filterTypes, setFilterTypes] = useState([
    { label: 'All', value: translate('upcoming_post.all') },
    { label: 'Custom', value: translate('upcoming_post.custom') },
  ]);
  const [selectedFilterType, setFilterType] = useState('All');
  const [startDate, setStartDate] = useState(fromDate);
  const [endDate, setEndDate] = useState(toDate);
  const [customStartSelected, setCustomStartSelected] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const theme = useTheme();

  const handleFilterChange = (event) => {
    let value = event.target.value;
    setFilterType(value);
    setDateBasedOnFilterType(value);
  };

  const setDateBasedOnFilterType = (type) => {
    if (type === 'All') {
      setErrorMessage('');
      let currentDate = new Date();
      let tDate = currentDate.setDate(currentDate.getDate() + 365);
      currentDate.setHours(0, 0, 0, 0);
      tDate = new Date(currentDate);
      setStartDate(new Date());
      setEndDate(tDate);
      handleApplyFilter(new Date(), tDate);
    } else if (type === 'Custom') {
      setStartDate(null);
      setEndDate(null);
      setCustomStartSelected(false);
      setErrorMessage('');
    }
  };

  const handleChangeDate = (date, type) => {
    setErrorMessage('');
    if (type === 'fromdate') {
      setStartDate(date);
      setEndDate(null);
      setCustomStartSelected(true);
    } else if (type === 'todate') {
      setEndDate(date);
    }

    if (type === 'todate' && checkForDateValidation(startDate, date)) {
      handleApplyFilter(startDate, date);
    }
  };

  const checkForDateValidation = (startDate, endDate) => {
    if (!startDate || !endDate) {
      setErrorMessage(translate('upcoming_post.start_and_end_date_validation'));
      return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setErrorMessage(translate('upcoming_post.start_date_validation'));
      return false;
    }
    return true;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
      <FormControl sx={{ minWidth: 150 }}>
        <InputLabel id="date_filter">{translate('upcoming_post.choose_date_filter')}</InputLabel>
        <Select
          value={selectedFilterType}
          label={translate('upcoming_post.choose_date_filter')}
          onChange={handleFilterChange}
        >
          {filterTypes.map((filter) => (
            <MenuItem value={filter.value} key={filter.value}>
              {filter.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Stack direction="row" sx={{ alignItems: 'center', gap: '1rem' }}>
        {selectedFilterType === 'Custom' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={translate('upcoming_post.start_date')}
              value={startDate}
              onChange={(newValue) => handleChangeDate(newValue, 'fromdate')}
              disablePast
            />
            <DatePicker
              label={translate('upcoming_post.end_date')}
              value={endDate}
              onChange={(newValue) => handleChangeDate(newValue, 'todate')}
              minDate={startDate || null}
              disabled={!customStartSelected}
            />
          </LocalizationProvider>
        )}
      </Stack>

      {errorMessage && <Typography sx={{ color: 'error.main', fontSize: '0.875rem' }}>{errorMessage}</Typography>}

      {startDate && endDate && (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon sx={{ color: 'primary.main' }} />
          <Typography>
            {translate('upcoming_post.filter_applied_from')}
            <span
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                color: theme.palette.primary.main,
                marginLeft: '4px',
                fontWeight: 600,
              }}
            >
              {CommonHelper.formatDateAndTime(startDate, 'date')}
            </span>{' '}
            {translate('common.to')}
            <span
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                color: theme.palette.primary.main,
                marginLeft: '4px',
                fontWeight: 600,
              }}
            >
              {CommonHelper.formatDateAndTime(endDate, 'date')}
            </span>
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default ScheduledPostFilter;
